import interpolateComponents from '@automattic/interpolate-components';
import { sprintf } from 'sprintf-js';

import { Box } from 'components/Box';
import { Text } from 'components/Text/Text';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { trackClicked } from 'utils/analytics/track/trackClicked';

import { StyledLink } from './Notifications.styled';

type Props = {
  notification: MappedNotification;
  dismiss: () => void;
};

export function NotificationContent({ notification, dismiss }: Props) {
  switch (notification.type) {
    case 'NEW_APPLICATIONS_BY_LISTING':
      return (
        <>
          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
                listing_id: notification.data.id,
                listing_type: notification.data.type,
                listing_name: notification.data.name,
                org_id: notification.data.orgId,
                application_count: notification.data.applications.length,
              });
              dismiss();
            }}
            to={`/admin/${
              notification.data.orgId
            }/${notification.data.type.toLowerCase()}s/${notification.data.id}`}
          >
            {notification.data.name}
          </StyledLink>
          <Box mt="4px">
            {interpolateComponents({
              mixedString: sprintf(
                notification.data.count > 1
                  ? getText(
                      '{{strong}}%(count)s{{/strong}} unread applications'
                    )
                  : getText(
                      '{{strong}}%(count)s{{/strong}} unread application'
                    ),
                {
                  count: notification.data.count,
                }
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Box>
        </>
      );

    case 'MISSING_PROF_SUMMARY':
      return (
        <>
          <Text fontWeight="bold">
            {getText('Make your resume stand out!')}
          </Text>

          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
              });
              dismiss();
            }}
            to="/dashboard/employment-profile"
            data-qa-id="professional-summary-notification-link"
          >
            {getText('Add a Professional Summary')}
          </StyledLink>

          <Text>{getText('to highlight your strengths to recruiters.')}</Text>
        </>
      );
    case 'NEW_MENTIONS_BY_APPLICATION': {
      const title =
        notification.data.listing.name.length > 70
          ? `${notification.data.listing.name.slice(0, 70)}&hellip;`
          : notification.data.listing.name;
      return (
        <StyledLink
          onClick={() => {
            trackClicked('User Menu Notification', {
              notification_type: notification.type,
              application_id: notification.data.id,
              listing_id: notification.data.listing.id,
              listing_type: notification.data.listing.type,
              listing_name: notification.data.listing.name,
              org_id: notification.data.orgId,
              mentions_count: notification.data.count,
            });
            dismiss();
          }}
          to={`/admin/${notification.data.orgId}/applications/${notification.data.id}`}
        >
          {notification.data.count === 1
            ? `${notification.data.mentionBy.name} wants you to review an applicant for ${title}`
            : `Your teammates want you to review an applicant for ${title}`}
        </StyledLink>
      );
    }

    case 'NEW_SUGGESTED_APPLICANTS_BY_JOB': {
      const title =
        notification.data.job.name.length > 70
          ? `${notification.data.job.name.slice(0, 70)}&hellip;`
          : notification.data.job.name;
      return (
        <>
          <StyledLink
            onClick={() => {
              trackClicked('User Menu Notification', {
                notification_type: notification.type,
                listing_id: notification.data.job.id,
                listing_type: 'JOB',
                listing_name: notification.data.job.name,
                org_id: notification.data.orgId,
                candidate_count: notification.data.count,
              });
              dismiss();
            }}
            to={`/admin/${notification.data.orgId}/jobs/${notification.data.job.id}/recommended-candidates`}
          >
            {title}
          </StyledLink>
          <Text>
            {interpolateComponents({
              mixedString: sprintf(
                getText(
                  '{{strong}}%(count)d{{/strong}} new recommended resumes'
                ),
                {
                  count: notification.data.count,
                }
              ),
              components: {
                strong: <strong />,
              },
            })}
          </Text>
        </>
      );
    }

    default:
      return null;
  }
}
